import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { generateAnnouncementContent, generateSocialPost } from '../../utils/announcementUtils';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

interface AnnouncementGeneratorProps { eventId: string; eventTitle: string; eventDate: string; onClose: () => void; }

const AnnouncementGenerator: React.FC<AnnouncementGeneratorProps> = ({ eventId, eventTitle, eventDate, onClose }) => {
  const [tab, setTab] = useState<'website' | 'social'>('website');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [socialPost, setSocialPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const lb = await (client.queries as any).calculateLeaderboard({ eventId });
        const leaderboard = parseLambdaResponse(lb?.data);

        // Find winner photo
        let winnerPhoto: { memberName: string } | null = null;
        try {
          let all: any[] = [];
          let nt: string | null = null;
          do { const r: any = await (client.models as any).CompetitionPhoto.list({ filter: { eventId: { eq: eventId } }, ...(nt ? { nextToken: nt } : {}) }); all = all.concat(r.data || []); nt = r.nextToken ?? null; } while (nt);
          const w = all.find((p: any) => p.status === 'WINNER');
          if (w) winnerPhoto = { memberName: w.memberName };
        } catch { /* skip */ }

        // Gather competition day stats. #79-class: page fully — a filtered
        // list({ limit: 1000 }) caps rows SCANNED, not returned, so
        // totalRegistrations could be silently undercounted.
        let stats: { totalRegistrations: number; totalSubmitters: number } | undefined;
        try {
          let regs: any[] = [];
          let regToken: string | null = null;
          do {
            const regsResult: any = await (client.models as any).CompetitionDayRegistration.list({
              filter: { eventId: { eq: eventId } },
              limit: 1000,
              ...(regToken ? { nextToken: regToken } : {}),
            });
            regs = regs.concat(regsResult.data || []);
            regToken = regsResult.nextToken ?? null;
          } while (regToken);
          const totalRegistrations = regs.length;
          // Count unique submitters from leaderboard
          const allEntries: any[] = Object.values(leaderboard?.categoryRankings || {}).flat();
          const uniqueSubmitters = new Set(allEntries.map((e: any) => e.competitorName)).size;
          stats = { totalRegistrations, totalSubmitters: uniqueSubmitters };
        } catch { /* non-critical */ }

        const ann = generateAnnouncementContent(eventTitle, eventDate, leaderboard, winnerPhoto, stats);
        setTitle(ann.title);
        setContent(ann.content);
        setSocialPost(generateSocialPost(eventTitle, eventDate, leaderboard, winnerPhoto, stats));
      } catch { setError('Failed to generate announcement'); }
      setLoading(false);
    };
    load();
  }, [eventId, eventTitle, eventDate]);

  const handlePost = async () => {
    setPosting(true);
    setError(null);
    try {
      const publishDate = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
      await (client.models as any).Announcement.create({ title, content, publishDate, category: 'CLUB_NEWS', isActive: true });
      setPosted(true);
    } catch (err: any) { setError(err.message || 'Failed to post announcement'); }
    setPosting(false);
  };

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(socialPost); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* fallback */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e: any) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Generate Announcement</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
          </div>

          {loading ? <p className="text-gray-500 py-8 text-center">Generating…</p> : (
            <>
              {/* Tabs */}
              <div className="flex gap-2 mb-4 border-b border-gray-200">
                <button onClick={() => setTab('website')} className={`pb-2 px-3 text-sm font-medium border-b-2 ${tab === 'website' ? 'border-fishing-blue text-fishing-blue' : 'border-transparent text-gray-500'}`}>Website Announcement</button>
                <button onClick={() => setTab('social')} className={`pb-2 px-3 text-sm font-medium border-b-2 ${tab === 'social' ? 'border-fishing-blue text-fishing-blue' : 'border-transparent text-gray-500'}`}>Social Media Post</button>
              </div>

              {error && <div className="bg-red-50 border border-red-200 rounded p-3 mb-4 text-sm text-red-800">{error}</div>}

              {tab === 'website' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input value={title} onChange={(e: any) => setTitle(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fishing-blue" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                    <textarea value={content} onChange={(e: any) => setContent(e.target.value)} rows={12} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fishing-blue font-mono" />
                  </div>
                  {posted ? (
                    <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">✅ Announcement posted successfully!</div>
                  ) : (
                    <button onClick={handlePost} disabled={posting || !title || !content} className="w-full py-2 bg-fishing-blue text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300">{posting ? 'Posting…' : 'Post Announcement'}</button>
                  )}
                </div>
              )}

              {tab === 'social' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Social Media Post</label>
                    <textarea value={socialPost} onChange={(e: any) => setSocialPost(e.target.value)} rows={10} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fishing-blue" />
                  </div>
                  <button onClick={handleCopy} className="w-full py-2 bg-gray-800 text-white rounded-md text-sm font-medium hover:bg-gray-900">{copied ? 'Copied! ✓' : 'Copy to Clipboard'}</button>
                  <p className="text-xs text-gray-500 text-center">Paste into Facebook, Instagram, or your preferred platform</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementGenerator;
